import fm from 'front-matter'
import type { Element, ElementContent, Root } from 'hast'
import { rawGithubUrl } from '@/lib/github'
import { vaultMediaUrl } from '@/lib/vaultAssets'

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|svg|webp|avif)$/i

export interface ResourceMeta {
  title?: string
  author?: string
  year?: string
  edition?: string
  publisher?: string
  isbn?: string
  type?: string
  code?: string
  coverImageUrl?: string
  getCopyUrl?: string
  /**
   * Where a book with no authored link can be found, one row per place — the
   * "Get a copy" menu. Only set when `getCopyUrl` is itself the ISBN search, so
   * an authored link is never buried under a menu of guesses.
   */
  copySources?: CopySource[]
}

/** One place to look for a book, searched on its ISBN. */
export interface CopySource {
  /** Which place — the menu prices the Amazon row (`lib/amazonPrice.ts`). */
  id: keyof typeof COPY_SOURCE_LOGOS
  label: string
  url: string
  /**
   * The place's own mark, served from `quiz/public` — see `COPY_SOURCE_LOGOS`.
   */
  logo: string
}

/**
 * Each place's own icon, copied from its site into `quiz/public/copy-sources/`
 * rather than hotlinked the way a Cowork publisher's logo is. There are only
 * three and they never change, and a hotlink would make merely *opening* the
 * menu send a request to each of them. An approximation of a mark would be an
 * invented brand (`components/cowork/EntityLogo.tsx`), so these are the files
 * the sites serve:
 *
 *   WorldCat — search.worldcat.org/favicons/android-chrome-192x192.png, at 64px
 *   Amazon — www.amazon.com/favicon.ico, its 48px frame
 *   The Actuarial Bookstore — the emblem from its wordmark, as the Store's
 *     seller logo (`quiz/public/store-sellers/actuarial-bookstore.png`)
 */
const COPY_SOURCE_LOGOS = {
  worldcat: '/copy-sources/worldcat.png',
  'actuarial-bookstore': '/copy-sources/actuarial-bookstore.png',
  amazon: '/copy-sources/amazon.png',
} as const

/**
 * The books The Actuarial Bookstore stocks, keyed by ISBN digits → the id of the
 * product's page (`/order_selection.aspx?id=…`). The bookstore's search is a
 * form post with no address of its own, so a link has to be to a product page,
 * and a product page is only worth linking when the bookstore has the book —
 * which is why this is a table read off the site, not a rule ("ACTEX books are
 * sold there"). A book is added once its ISBN turns up in a search of the
 * bookstore (the ISBN may be any edition's: the page offers print and digital
 * together); one it doesn't list gets no row.
 *
 * Last read 2026-10-05.
 */
const ACTUARIAL_BOOKSTORE_PRODUCTS: Readonly<Record<string, string>> = {
  '9781647563165': '453151265', // R for Actuaries and Data Scientists (Fannin)
  '9781647563226': '453151403', // Probability for Risk Management (Hassett)
  '9781625424723': '453151268', // Probability and Statistics with Applications (Asimow)
  '9798890161871': '453151387', // Financial Mathematics: Theory and Practice (Brown & Kopp)
}

function extractUrl(value: string): string | undefined {
  const m = value.match(/\(([^)]+)\)/)
  return m ? m[1] : (value.startsWith('http') ? value : undefined)
}

/**
 * A library-catalogue search for a book, built from the ISBN its page already
 * transcribes.
 *
 * This exists because the alternative kept being wrong. WorldCat's
 * `/title/<n>` paths take an OCLC control number, so a title-and-author slug
 * written into that position ("…/title/mathematical-interest-theory-vaaler")
 * is a guess, and every one of those guesses 404s. Even a real control number
 * is a poor thing to author by hand: it pins one manifestation, so it can
 * quietly point at a different edition than the page describes.
 *
 * An ISBN search cannot be wrong in either way — it is derived from a number
 * taken off the book itself, and it names a search rather than claiming a
 * record exists. The URL shape is the one OCLC documents for deep links
 * (`worldcat.org/isbn/<isbn>`, which redirects here).
 *
 * Returns nothing unless the ISBN is a well-formed 10- or 13-character one:
 * no button at all beats a button onto an empty result page.
 */
export function librarySearchUrl(isbn?: string): string | undefined {
  return copySources(isbn)[0]?.url
}

/** The ISBN with its hyphens and spaces gone, or nothing if it isn't one. */
export function isbnDigits(isbn?: string): string | undefined {
  if (!isbn) return undefined
  const digits = isbn.replace(/[\s-]/g, '').toUpperCase()
  return /^(?:\d{9}[\dX]|\d{13})$/.test(digits) ? digits : undefined
}

/**
 * Every place the "Get a copy" menu offers for a book, library first — the
 * same ISBN search `librarySearchUrl` returns leads the list, then The Actuarial
 * Bookstore (only for a book it stocks) and Amazon. WorldCat and Amazon are a
 * *search* on the ISBN, never a record id, for the reason `librarySearchUrl`
 * gives: a search derived from the book's own number can't point at the wrong
 * edition or a page that doesn't exist. The bookstore has no search address, so
 * its row is the product page from `ACTUARIAL_BOOKSTORE_PRODUCTS`.
 *
 * Empty unless the ISBN is well formed.
 */
export function copySources(isbn?: string): CopySource[] {
  const digits = isbnDigits(isbn)
  if (!digits) return []
  const bookstoreId = ACTUARIAL_BOOKSTORE_PRODUCTS[digits]
  return [
    {
      id: 'worldcat',
      label: 'WorldCat',
      url: `https://search.worldcat.org/search?q=bn%3A${digits}`,
      logo: COPY_SOURCE_LOGOS.worldcat,
    },
    ...(bookstoreId
      ? [{
          id: 'actuarial-bookstore' as const,
          label: 'Actuarial Bookstore',
          url: `https://www.actuarialbookstore.com/order_selection.aspx?id=${bookstoreId}`,
          logo: COPY_SOURCE_LOGOS['actuarial-bookstore'],
        }]
      : []),
    {
      id: 'amazon',
      label: 'Amazon',
      url: `https://www.amazon.com/s?k=${digits}&i=stripbooks`,
      logo: COPY_SOURCE_LOGOS.amazon,
    },
  ]
}

export function parseResourceMeta(raw: string): ResourceMeta {
  let attrs: Record<string, unknown> = {}
  try {
    attrs = fm<Record<string, unknown>>(raw).attributes ?? {}
  } catch {
    return {}
  }
  const str = (v: unknown) => (v != null ? String(v).trim() || undefined : undefined)

  const isbn = str(attrs['ISBN'])
  // An authored link is where the source actually *is* — a publisher's PDF, a
  // standards body's page. Only when a page names no such place (a textbook
  // that is simply for sale) does the card fall back to finding it in a
  // library.
  const linkStr = str(attrs['Find at your local library at']) ?? str(attrs['Available from'])
  const authoredUrl = linkStr ? extractUrl(linkStr) : undefined
  const isbnSources = authoredUrl ? [] : copySources(isbn)
  const getCopyUrl = authoredUrl ?? isbnSources[0]?.url

  const body = raw.replace(/^---\n[\s\S]*?\n---\n?/, '')
  const imgMatch = /!\[\[([^\]|]+)\]\]/.exec(body)
  let coverImageUrl: string | undefined
  if (imgMatch) {
    const imgPath = imgMatch[1].trim()
    if (IMAGE_EXT_RE.test(imgPath)) {
      const resolved = imgPath.includes('/') ? imgPath : `Media/Attachments/${imgPath}`
      coverImageUrl = vaultMediaUrl(resolved) ?? rawGithubUrl(resolved)
    }
  }

  return {
    title: str(attrs['Title']),
    author: str(attrs['Authors']) ?? str(attrs['Author']),
    year: str(attrs['Year']),
    edition: str(attrs['Edition']),
    publisher: str(attrs['Publisher']),
    isbn,
    type: str(attrs['Type']),
    code: str(attrs['Code']),
    coverImageUrl,
    getCopyUrl,
    copySources: isbnSources.length > 0 ? isbnSources : undefined,
  }
}

// Remove the first cover image embed — it is shown in the metadata card instead.
export function stripFirstCoverImage(md: string): string {
  return md.replace(/!\[\[[^\]|]+\.(png|jpe?g|gif|svg|webp|avif)\]\][ \t]*\n?/i, '')
}

/**
 * One exam a resource is a reading for, as its page's `> [!info] On the
 * syllabus` callout states it (docs/resource-pages.md): the exam page linked,
 * the name it was linked under, and what of the resource that exam assigns.
 */
export interface SyllabusAssignment {
  /** The exam page's vault name — "Exam MAS-I (CAS)". */
  exam: string
  /** How the callout names it — "Exam MAS-I". */
  label: string
  /** The rest of the bullet, after its dash — objectives and chapters. */
  detail?: string
}

const SYLLABUS_CALLOUT_RE = /^>\s*\[!info\][-+]?\s+On the syllabus\s*$/i
const SYLLABUS_ENTRY_RE = /^>\s*[-*+]\s+\[\[([^\]|]+)(?:\|([^\]]+))?\]\]\s*(?:[—–-]\s*(.+))?$/

/**
 * Lift the "On the syllabus" callout out of a resource page, so the app can
 * draw it as its own card under the metadata card rather than as a callout
 * part-way down the article. The callout is removed only when every line of
 * it reads as an entry — anything the parser doesn't understand is left on
 * the page for the article to render as written.
 */
export function extractSyllabusAssignments(md: string): {
  markdown: string
  assignments: SyllabusAssignment[]
} {
  const lines = md.split('\n')
  const start = lines.findIndex(line => SYLLABUS_CALLOUT_RE.test(line))
  if (start < 0) return { markdown: md, assignments: [] }
  let end = start + 1
  const assignments: SyllabusAssignment[] = []
  for (; end < lines.length && lines[end].startsWith('>'); end++) {
    if (lines[end].replace(/^>\s*/, '') === '') continue
    const m = SYLLABUS_ENTRY_RE.exec(lines[end])
    if (!m) return { markdown: md, assignments: [] }
    const exam = m[1].trim()
    const detail = m[3]?.trim()
    assignments.push({
      exam,
      label: (m[2] ?? exam).trim(),
      ...(detail ? { detail: detail.charAt(0).toUpperCase() + detail.slice(1) } : {}),
    })
  }
  if (assignments.length === 0) return { markdown: md, assignments: [] }
  // Take one blank line after the callout with it, so no double gap is left.
  if (end < lines.length && lines[end].trim() === '') end++
  return {
    markdown: [...lines.slice(0, start), ...lines.slice(end)].join('\n'),
    assignments,
  }
}

export function preprocessResourceMarkdown(raw: string): string {
  return extractSyllabusAssignments(stripFirstCoverImage(raw)).markdown
}

// "1", "1.4", "1.4.2", "0.1", "1.01", "A.2", "1." — a chapter/section label.
// A lone letter isn't one ("A trending procedure…"), nor is a year ("2009 …").
const SECTION_LABEL_RE = /^(?:\d{1,3}(?:\.\d+)*|[A-Z](?:\.\d+)+)\.?\s+\S/

/** A list item that numbers itself — drawn without a marker. */
export const OUTLINE_ITEM_CLASS = 'outline-item'
/** A list every item of which numbers itself — drawn flush, without indent. */
export const OUTLINE_LIST_CLASS = 'outline-list'

/** An item's own words, without the lists nested under it. */
function ownText(node: Element | ElementContent): string {
  if (node.type === 'text') return node.value
  if (node.type !== 'element' || node.tagName === 'ul' || node.tagName === 'ol') return ''
  return node.children.map(ownText).join('')
}

function addClass(el: Element, name: string) {
  const cls = el.properties.className
  const list = Array.isArray(cls) ? cls.map(String) : typeof cls === 'string' ? cls.split(/\s+/) : []
  el.properties.className = [...list, name]
}

function markOutline(node: Root | Element) {
  for (const child of node.children) {
    if (child.type !== 'element') continue
    markOutline(child)
    if (child.tagName !== 'ul' && child.tagName !== 'ol') continue
    const items = child.children.filter((c): c is Element => c.type === 'element' && c.tagName === 'li')
    let labelled = 0
    for (const li of items) {
      if (!SECTION_LABEL_RE.test(ownText(li).trimStart())) continue
      addClass(li, OUTLINE_ITEM_CLASS)
      labelled++
    }
    if (items.length > 0 && labelled === items.length) addClass(child, OUTLINE_LIST_CLASS)
  }
}

/**
 * A book's table of contents already numbers itself ("1.4.2 Accumulated
 * Value…"), so a list marker beside such a row is a second, conflicting
 * numbering. This rehype plugin marks each row that carries its own section
 * label, and each list made only of such rows, so `RESOURCE_ARTICLE_CLASS`
 * can drop their markers. It decides row by row, not page by page: an ASOP's
 * numbered sections and the description bullets under them sit on one page,
 * and both are drawn the way they are on every other resource page.
 */
export function rehypeOutlineItems() {
  return (tree: Root) => markOutline(tree)
}

// Applied to the WikiArticle of every resource page, so one page's headings
// and lists look like the next one's: chapter headings sized closer to the
// entries they head, and a self-numbered row (rehypeOutlineItems) drawn
// without a marker, its nesting carried by the indent.
export const RESOURCE_ARTICLE_CLASS =
  'prose-h2:text-lg prose-h2:mt-5 prose-h2:mb-1 ' +
  '[&_li.outline-item]:list-none [&_li.outline-item]:pl-0 ' +
  '[&_.outline-list]:pl-0 [&_li>.outline-list]:pl-4'
